import { useEffect, useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem, Alert, CircularProgress,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { listGoodsReceipts, getGoodsReceipt, type GoodsReceiptLine } from '../../api/receptii'
import { createSupplierReturn, createReturnCreditNote, type PurchaseOrderListItem } from '../../api/comenziFurnizor'
import { errorMessage } from '../../api/client'

async function loadReceiptLinesForOrder(orderId: string): Promise<GoodsReceiptLine[]> {
  const receipts = await listGoodsReceipts()
  const forOrder = receipts.filter((r) => r.purchaseOrderId === orderId)
  const details = await Promise.all(forOrder.map((r) => getGoodsReceipt(r.id)))
  return details.flatMap((d) => d.lines)
}

export default function SupplierReturnDialog({ order, onClose, onReturned }: {
  order: PurchaseOrderListItem | null; onClose: () => void; onReturned: () => void
}) {
  const open = order !== null
  const qc = useQueryClient()
  const { data: receiptLines } = useQuery({
    queryKey: ['achizitii', 'receptii', 'linii-pentru-comanda', order?.id ?? null],
    queryFn: () => loadReceiptLinesForOrder(order!.id), enabled: open,
  })

  const [returnDate, setReturnDate] = useState(new Date().toISOString().slice(0, 10))
  const [reason, setReason] = useState('')
  const [lineId, setLineId] = useState('')
  const [quantity, setQuantity] = useState('')
  const [createdReturnId, setCreatedReturnId] = useState<string | null>(null)

  useEffect(() => {
    if (open) { setReturnDate(new Date().toISOString().slice(0, 10)); setReason(''); setLineId(''); setQuantity(''); setCreatedReturnId(null) }
  }, [open])

  const mutation = useMutation({
    mutationFn: () => createSupplierReturn({
      supplierId: order!.supplierId, returnDate, reason,
      notes: null, lines: [{ goodsReceiptLineId: lineId, quantityReturned: Number(quantity), reason: null }],
    }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['achizitii', 'comenzi'] })
      onReturned()
      setCreatedReturnId(r.id)
    },
  })

  const creditNoteMutation = useMutation({
    mutationFn: () => createReturnCreditNote(createdReturnId!),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['achizitii', 'facturi'] }); onClose() },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Retur furnizor — comandă {order?.number}</DialogTitle>
      <DialogContent>
        {createdReturnId ? (
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Alert severity="success">Retur înregistrat. Generați și nota de credit contabilă (FR-ACF-006)?</Alert>
            {creditNoteMutation.isError && <Alert severity="error">{errorMessage(creditNoteMutation.error)}</Alert>}
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField select label="Linie recepționată" value={lineId} onChange={(e) => setLineId(e.target.value)} fullWidth>
              {(receiptLines ?? []).map((l) => (
                <MenuItem key={l.id} value={l.id!}>{l.articleCode} — {l.articleDescription} ({l.quantityReceived} recepționat{l.lotNumber ? `, lot ${l.lotNumber}` : ''})</MenuItem>
              ))}
            </TextField>
            <Stack direction="row" spacing={2}>
              <TextField type="date" label="Data returului" value={returnDate} onChange={(e) => setReturnDate(e.target.value)}
                sx={{ width: 180 }} slotProps={{ inputLabel: { shrink: true } }} />
              <TextField type="number" label="Cantitate returnată" value={quantity} onChange={(e) => setQuantity(e.target.value)} sx={{ flexGrow: 1 }} />
            </Stack>
            <TextField label="Motiv" value={reason} onChange={(e) => setReason(e.target.value)} fullWidth multiline minRows={2} />
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {createdReturnId ? (
          <>
            <Button onClick={onClose}>Fără notă de credit</Button>
            <Button variant="contained" disabled={creditNoteMutation.isPending} onClick={() => creditNoteMutation.mutate()}
              startIcon={creditNoteMutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
              Generează nota de credit
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose}>Renunță</Button>
            <Button variant="contained" disabled={!lineId || !quantity || !reason || mutation.isPending} onClick={() => mutation.mutate()}
              startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
              Înregistrează returul
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
